import { combineReducers } from 'redux';
import { createReducer } from 'redux-act';
import { createSelector } from 'reselect';
import { selectCurrentId } from '@gandi/redux-sharing';
import { createResource } from '@gandi/resource/redux';
import { selectSharingIdProp, selectRaw as selectRawState } from './utils';
export const DEFAULT_PAGE = 1;
export const DEFAULT_PER_PAGE = 10;
export const DEFAULT_SORT_BY = '-id';
export const DEFAULT_FILTERS = Object.freeze({});
export const hashString = str => String(str.split('').reduce((prevHash, currVal) => (prevHash << 5) - prevHash + currVal.charCodeAt(0), 0));
export const computeLabel = props_ => {
  const props = props_ || {};
  let paramsHash;
  if (props.exportParams) {
    const {
      exportParams: rawExportParams
    } = props;
    const exportParams = {
      ...rawExportParams,
      startAt: rawExportParams.startAt?.toISOString(),
      endAt: rawExportParams.endAt?.toISOString()
    };
    paramsHash = hashString(JSON.stringify(exportParams));
  } else {
    paramsHash = hashString(JSON.stringify({
      page: props.page || DEFAULT_PAGE,
      perPage: props.perPage || DEFAULT_PER_PAGE,
      sortBy: props.sortBy || DEFAULT_SORT_BY,
      filters: !props.filters ? DEFAULT_FILTERS : {
        mustPay: props.filters.mustPay,
        canCancel: props.filters.canCancel,
        type: [].concat(props.filters.type || []).sort(),
        status: [].concat(props.filters?.status || []).sort(),
        paymentType: [].concat(props.filters?.paymentType || []).sort(),
        ...props.filters
      }
    }));
  }
  return `${props.sharingId}.${paramsHash}`;
};
export const selectComputedLabel = (_, props) => computeLabel(props);
export const selectOrderIdProp = (_, props) => props.orderId;
export const resource = createResource('billing.billing.orders');
export const selectIsFetching = (state, props) => resource.selectIsFetching(state, computeLabel(props));
export const selectIsLoaded = (state, props) => resource.selectIsLoaded(state, computeLabel(props));
export const selectIsFailure = (state, props) => resource.selectIsFailure(state, computeLabel(props));
export const selectIsToRefetch = (state, props) => resource.selectIsToRefetch(state, computeLabel(props));
export const selectIsFetchingOne = (state, props) => resource.selectIsFetchingOne(state, {
  label: String(props.orderId)
});
export const selectIsLoadedOne = (state, props) => resource.selectIsLoadedOne(state, {
  label: String(props.orderId)
});
export const selectIsFailureOne = (state, props) => resource.selectIsFailureOne(state, {
  label: String(props.orderId)
});
export const selectIsToRefetchOne = (state, props) => resource.selectIsToRefetchOne(state, {
  label: String(props.orderId)
});
export const selectRawSummaries = createSelector([selectRawState], raw => raw.orders.summaries);
export const selectSummariesMeta = createSelector([selectRawSummaries, selectSharingIdProp, selectComputedLabel], (raw, sharingId, label) => raw[sharingId] ? raw[sharingId][label.split('.')[1]] : undefined);
export const selectSummariesTotalCount = createSelector([selectSummariesMeta], (meta = {}) => meta.totalCount);
export const selectSummaries = createSelector([selectSummariesMeta], (meta = {}) => meta.data);
export const selectRawOrders = createSelector([selectRawState], raw => raw.orders.details);
export const selectOrder = createSelector([selectRawOrders, selectOrderIdProp], (orders, orderId) => orders[orderId]);
const mapFiltersToParams = {
  mustPay: 'must_pay',
  canCancel: 'can_cancel',
  paymentType: 'payment_type',
  status: 'status',
  type: 'type',
  productType: 'product_type',
  orderId: 'order_id',
  minDate: 'min_date',
  maxDate: 'max_date',
  minAmount: 'min_amount',
  maxAmount: 'max_amount',
  invoiceNumber: 'invoice_num'
};
export const fetchOrders = options => (dispatch, getState) => {
  const currentId = selectCurrentId(getState());
  const sharingId = options.sharingId || currentId;
  const page = options.page || DEFAULT_PAGE;
  const perPage = options.perPage || DEFAULT_PER_PAGE;
  const sortBy = options.sortBy || DEFAULT_SORT_BY;
  const {
    maxDate,
    ...rest
  } = options.filters || {};
  const filters = {
    ...rest,
    maxDate: maxDate && new Date(Date.parse(maxDate) + 86400000).toISOString().split('T')[0]
  };
  if (typeof sharingId !== 'string') {
    return Promise.reject(new Error('[billing.orders] `fetchOrders` called without `sharingId` option' + ' nor @gandi/redux-sharing dep/state.'));
  }
  const label = computeLabel({
    page,
    perPage,
    sortBy,
    filters: options.filters || Object.freeze({}),
    sharingId
  });
  const params = {
    ...(options.params || {}),
    ...Object.keys(filters).reduce((acc, filter) => ({
      ...acc,
      [mapFiltersToParams[filter] || filter]: filters[filter]
    }), Object.freeze({})),
    page,
    per_page: perPage,
    sort_by: sortBy,
    sharing_id: sharingId
  };
  return dispatch(resource.fetch('/billing/orders', {
    ...options,
    label,
    params,
    on200: response => response,
    not200: response => Promise.reject(response)
  }));
};
export const fetchOrder = (orderId, options = {
  sharingId: undefined,
  params: undefined
}) => (dispatch, getState) => {
  const label = String(orderId);
  const currentId = selectCurrentId(getState());
  const sharingId = options.sharingId || currentId;
  if (!sharingId) {
    return Promise.reject(new Error('[billing.orders] `fetchOrder` called without `sharingId` option' + ' nor @gandi/redux-sharing dep/state.'));
  }
  return dispatch(resource.fetchOne(`/billing/orders/${orderId}`, {
    ...options,
    label,
    params: {
      ...(options.params || {}),
      sharing_id: sharingId
    },
    on200: response => response,
    not200: response => Promise.reject(response)
  }));
};
export const fetchExportOrders = options => dispatch => {
  const {
    exportParams,
    sharingId,
    ...fetchOptions
  } = options;
  const {
    format
  } = exportParams;
  const label = computeLabel({
    sharingId,
    exportParams
  });
  let ordersParams;
  if (exportParams.ordersIds) {
    const {
      ordersIds
    } = exportParams;
    ordersParams = {
      id: ordersIds.map(number => number.toString())
    };
  } else {
    const {
      startAt,
      endAt
    } = exportParams;
    ordersParams = {
      start_at: startAt.toISOString().split('T')[0],
      end_at: endAt.toISOString().split('T')[0]
    };
  }
  if (!sharingId) {
    return Promise.reject(new Error('[billing.exportOrders] `fetchExportOrders` called without `sharingId` option'));
  }
  return dispatch(resource.fetch(`/billing/exports/order`, {
    ...fetchOptions,
    label,
    params: {
      ...ordersParams,
      ...(options.params || {}),
      sharing_id: sharingId,
      format
    },
    on200: response => response,
    not200: response => Promise.reject(response)
  }));
};
export const cancelOrder = (orderId, options = {
  params: undefined,
  sharingId: undefined
}) => (dispatch, getState) => {
  const label = `cancel-${orderId}`;
  const currentId = selectCurrentId(getState());
  const sharingId = options.sharingId || currentId;
  if (!sharingId) {
    return Promise.reject(new Error('[billing.orders] `cancelOrder` called without `sharingId` option' + ' nor @gandi/redux-sharing dep/state.'));
  }
  return dispatch(resource.createOne(`/billing/orders/${orderId}/actions`, {}, {
    label,
    ...options,
    body: JSON.stringify({
      action: 'cancel'
    }),
    params: {
      ...(options.params || {}),
      sharing_id: sharingId
    },
    on200: response => {
      dispatch(resource.invalidate());
      return response;
    },
    not200: response => Promise.reject(response)
  }));
};
export const summariesReducer = createReducer({
  [resource.fetch.success.getType()]: (state, {
    response: {
      data,
      headers
    },
    label
  }) => {
    const splittedLabel = label.split('.');
    const sharingId = splittedLabel[0];
    const hash = splittedLabel[1];
    return {
      ...state,
      [sharingId]: {
        ...(state[sharingId] || {}),
        [hash]: {
          totalCount: headers.get('x-total-count'),
          data
        }
      }
    };
  }
}, {});
export const detailsReducer = createReducer({
  [resource.fetchOne.success.getType()]: (state, {
    response: {
      data,
      headers
    },
    label
  }) => {
    const disposition = headers && headers.get('Content-Disposition');
    return disposition && disposition.startsWith('attachment') ? state : {
      ...state,
      [label]: data
    };
  }
}, {});
export default combineReducers({
  summaries: summariesReducer,
  details: detailsReducer
});